import {
  getIssueBySeverity,
  IssueBySeverity,
  messageFromIssues,
  Severity,
} from "@profiler/diagnostic";
import { AlertType } from "@profiler/ui";

import { makeRequest } from "../api/request";
import { ConfigUrlsTypes } from "../types/configUrls";

type ApplyFhirPathResponse = {
  type: AlertType;
  outcome?: string;
  message?: string;
};

const alertTypeFromIssues = (issueBySeverity: IssueBySeverity): AlertType => {
  if (
    issueBySeverity[Severity.Fatal].length ||
    issueBySeverity[Severity.Error].length
  ) {
    return AlertType.Error;
  }
  if (issueBySeverity[Severity.Warning].length) {
    return AlertType.Info;
  }

  return AlertType.Success;
};

const toParameters = (
  resource: Record<string, any>,
  expression: string,
  path?: string
) => ({
  ResourceType: "Parameters",
  parameter: [
    {
      name: "fhirPath",
      valueString: expression,
    },
    ...(path
      ? [
          {
            name: "path",
            valueString: path,
          },
        ]
      : []),
    {
      name: "resource",
      resource,
    },
  ],
});

export const applyFhirPath = async (
  content: string,
  expression: string,
  path?: string
): Promise<Promise<ApplyFhirPathResponse>> => {
  try {
    if (!expression)
      return {
        type: AlertType.Error,
        message: "Expression cannot be empty",
      };

    let resource = {};
    try {
      resource = JSON.parse(content);
    } catch (e) {
      return {
        type: AlertType.Error,
        message: "Invalid resource",
      };
    }

    const serverUrl = localStorage.getItem(ConfigUrlsTypes.FhirBaseApiUrl);
    if (!serverUrl)
      return {
        type: AlertType.Error,
        message: "FHIR server URL not specified",
      };

    const parameters = toParameters(resource, expression, path);
    const response = await makeRequest({
      url: `${serverUrl}/$apply-fhirpath`,
      body: parameters,
    });

    const { body } = response;
    const outcome = body.parameter?.[0]?.valueString;
    const issueBySeverity = getIssueBySeverity(body?.issue);

    return {
      outcome,
      message: messageFromIssues(issueBySeverity),
      type: alertTypeFromIssues(issueBySeverity),
    };
  } catch (error) {
    return {
      message: (error as { message: string }).message,
      type: AlertType.Error,
    };
  }
};
